<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { PRODUCT_TYPE } from "@/util/constants";
import { XCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIModal, UIModalContent, UIModalHeader } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Props interface for the mixed custom object error modal
 */
interface Props {
  show: boolean;
  existingObjectName: string; // e.g., "Hospitals"
  attemptedObjectName: string; // e.g., "Doctors"
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:close": [value: boolean];
}>();

/**
 * Supporting text with dynamic object names
 */
const { t } = useI18n();

const store = useAppStore();

const productLabel = computed(() => {
  const currentProduct = store.product;
  const label =
    currentProduct === PRODUCT_TYPE.SURVEY
      ? t("survey.typeOfBuilder")
      : currentProduct === PRODUCT_TYPE.QUIZ
      ? t("quiz.typeOfBuilder")
      : t("form.typeOfBuilder");
  return String(label).toLowerCase();
});

const capitalizeFirst = (value: string): string => {
  if (!value || typeof value !== "string") {
    return value as unknown as string;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const supportingText = computed(() => {
  const company = t("common.business");
  const opportunity = t("common.opportunity");
  const attempted: string = capitalizeFirst((props.attemptedObjectName || "").trim());
  const list: string[] = [company, opportunity];

  if (
    attempted &&
    attempted.toLowerCase() !== company.toLowerCase() &&
    attempted.toLowerCase() !== opportunity.toLowerCase()
  ) {
    list.push(attempted);
  }

  return `Fields from "${capitalizeFirst(props.existingObjectName)}" already exist in this ${
    productLabel.value
  }. Only one associated object fields (${list.join(" / ")}) can be added to the ${
    productLabel.value
  } alongside ${t("common.contact")} at a time.`;
});

/**
 * Modal title
 */
const modalTitle = "You cannot add this Field";

/**
 * Handle close action
 */
const handleClose = () => {
  emit("update:close", false);
};
</script>

<template>
  <UIModal :show="show" @update:show="handleClose">
    <template #header>
      <UIModalHeader
        id="mixed-custom-object-error-modal-header"
        :title="modalTitle"
        :icon="XCircleIcon"
        type="error"
        @close="handleClose"
      />
    </template>

    <UIModalContent>
      <div class="text-sm text-gray-600">
        {{ supportingText }}
      </div>
    </UIModalContent>
  </UIModal>
</template>
